/**
 * The wine list for the guests (Getränkekarte, docs/15-getraenkekarte.md): which wines, in which
 * sections, in which order, with what per wine. Only data; the app and the preview lay it out.
 *
 * Rules:
 *   - The card has two parts: wines by the glass (Offene Weine), then by the bottle (Flaschenweine).
 *     Open are the wines with glass prices, always grouped by colour; a wine can be in both parts.
 *   - A wine marked „Nicht auf der Karte“ (offCard) is in neither part.
 *   - In the bottle part is every wine with a sale price and, unless asked otherwise, bottles in stock.
 *     A wine drunk up disappears by itself; a new one appears once it has a price.
 *   - Vintages and sizes of one wine stay together, as one entry with several lines.
 *   - Within a group the card number decides, then the name; wines without a number come last.
 *   - A keg is on the card only by the glass, never as a whole, even with a sale price.
 */
import { WINE_COLORS, WINE_MEASURES, isProfiled } from "./profile-wine.ts";
import { ADDITIVES, allergenNames, profileOf, sizeOf } from "./drinks.ts";
import { DEFAULT_RUBRICS, PART_TITLES, RECIPE_SECTION, additiveNumber, freeKind, freeOfAlcohol, partOrder, recipePlace } from "./menu.ts";
import type { Recipe, Rubric } from "./menu.ts";
import { PROFILES, PROFILE_TITLES, isBulk, profileById } from "./profiles.ts";
import type { Profile } from "./profile.ts";
import { measureName } from "./measures.ts";
import type { CustomMeasure } from "./measures.ts";
import type { ExistingArticle } from "./catalog-import.ts";
import { glassSize } from "./glass.ts";
import type { GlassPrice } from "./glass.ts";

/** How the wines are grouped (docs/15-getraenkekarte.md, Gliederung). */
export type CardGrouping = "color" | "colorOrigin" | "origin" | "number";

export interface CardOptions {
  grouping: CardGrouping;
  /** Also wines without bottles in stock (e.g. to print before a delivery). */
  withoutStock?: boolean;
  /**
   * A private cellar (Kellerliste): every wine in stock, prices or not, no open wines; each line
   * carries the bottles in stock and the drinking window instead of a price.
   */
  private?: boolean;
  /** The workspace's own names for measures („Schoppen“ for 500 ml), the last level of measures.ts. */
  measures?: readonly CustomMeasure[];
  /** Glass sizes as decimals, „0,25 l“ instead of „1/4 l“ (B5, a setting of the workspace). */
  decimal?: boolean;
  /**
   * The cellar list: the profiles on it, a part each (docs/17-getraenke.md, Oberfläche). Default:
   * wine, spirits and further drinks; beer and alcohol-free are off until switched on.
   */
  profiles?: readonly string[];
  /** Prepared drinks (Spritz, Gin Tonic, Schorle) for the guests' card. */
  recipes?: readonly Recipe[];
  /** The owner's rubrics; default Aperitif, Longdrinks und Cocktails, Digestif. */
  rubrics?: readonly Rubric[];
  /** The owner's order of the parts (keys: „offen“, „bier“, „rubric:aperitif“ …). */
  order?: readonly string[];
  /** Own headings: default title → own („Sekt“ → „Sekt & Champagner“). */
  headings?: Readonly<Record<string, string>>;
}

/** The profiles on a cellar list unless the cellar chose otherwise. */
export const CELLAR_PROFILES: readonly string[] = ["wein", "spirituosen", "weitere"];

/** One vintage and size of a wine, or one glass of it: a line with year, size and price. */
export interface CardLine {
  /** The wine (article) of this line. */
  article: string;
  listNo: string | null;
  vintage: number | null;
  /** "0,75 l", "Magnum 1,5 l" for a bottle; "1/8 l", "5 cl" for a glass. */
  size: string;
  /** Only for a glass: its size in ml. */
  ml?: number;
  /** Only for a bottle: its size in ml. */
  bottle?: number;
  /** Only for a bottle: the profile's usual size, shown without a size; 0: every size is shown (beer). */
  usual?: number;
  /** Cents. */
  /** Cents; null on the list of a private cellar. */
  price: number | null;
  /** Bottles in stock; only on the list of a private cellar. */
  bottles?: number;
  /** Drinking window, years; only on the list of a private cellar. */
  drinkFrom?: number | null;
  drinkTo?: number | null;
}

/** A wine on the card: producer and name once, its vintages and sizes as lines. */
export interface CardEntry {
  producer: string;
  wine: string;
  color: string;
  country: string | null;
  region: string | null;
  appellation: string | null;
  grapes: string[];
  taste: string | null;
  organic: boolean;
  text: string | null;
  lines: CardLine[];
  /** Footnote numbers of its additives (menu.ts, additiveNumber). */
  notes?: number[];
  allergens?: string[];
  /** At most 0,5 % vol. (for the rule on alcohol-free drinks). */
  alcoholFree?: boolean;
  /** A prepared drink: no producer. */
  recipe?: boolean;
  /** Not a wine: no vintages, each line says its size („0,3 l“, „Flasche 0,33 l“). */
  plain?: boolean;
  /** More to say in the facts line („vom Fass“). */
  extra?: string[];
  /** Quality level of the label („Prädikatswein Kabinett“, „DOCG“); null when its vintages differ. */
  quality?: string | null;
  /** „Restzucker 7,2 g/l · Säure 7,8 g/l · pH 3,2“; null when its vintages differ or nothing is known. */
  analysis?: string | null;
}

const decimal = (n: number) => String(n).replace(".", ",");

/** The analysis of a wine as the card says it, or null. */
export function analysisOf(f: Record<string, unknown>): string | null {
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const parts = [
    n(f.sugar) !== null ? `Restzucker\u00a0${decimal(n(f.sugar)!)}\u00a0g/l` : null,
    n(f.acidity) !== null ? `Säure\u00a0${decimal(n(f.acidity)!)}\u00a0g/l` : null,
    n(f.ph) !== null ? `pH\u00a0${decimal(n(f.ph)!)}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

export interface CardGroup {
  /** Null when the section is not divided further. */
  title: string | null;
  entries: CardEntry[];
}

export interface CardSection {
  title: string;
  groups: CardGroup[];
}

/** "Offene Weine", "Flaschenweine", "Biere", a rubric … */
export interface CardPart {
  title: string;
  sections: CardSection[];
  /** „offen“, „flasche“, „bier“, „rubric:aperitif“; on the cellar list the profile. */
  key?: string;
  /** The open wines: one column per glass size. */
  open?: boolean;
  /** A line under the title („je Flasche 0,75 l, wenn nicht anders angegeben“, „Enthält Sulfite“). */
  note?: string;
  /** Allergens every entry of the part has; said once under the title (`partNote`). */
  common?: string[];
}

/** The order of colours on a card: the aperitif first, the sweet ones last. */
export const CARD_COLORS: readonly string[] = ["Schaumwein", "Weiß", "Rosé", "Orange", "Rot", "Süßwein", "Likörwein"];
const SECTION_TITLE: Record<string, string> = {
  Schaumwein: "Sekt",
  Weiß: "Weißweine",
  Rosé: "Roséweine",
  Orange: "Orangeweine",
  Rot: "Rotweine",
  Süßwein: "Süßweine",
  Likörwein: "Likörweine",
};
const COUNTRY_ORDER = ["Deutschland", "Österreich", "Schweiz", "Frankreich", "Italien", "Spanien", "Portugal"];

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);
const num = (listNo: string | null) => (listNo && /^\d+/.test(listNo) ? parseInt(listNo, 10) : Infinity);
const byNumber = (a: { listNo: string | null; key: string }, b: { listNo: string | null; key: string }) =>
  num(a.listNo) - num(b.listNo) || (a.listNo ?? "").localeCompare(b.listNo ?? "", "de", { numeric: true }) || a.key.localeCompare(b.key, "de");
const countryRank = (c: string | null) => (c === null ? 999 : COUNTRY_ORDER.indexOf(c) === -1 ? 100 : COUNTRY_ORDER.indexOf(c));
const byCountry = (a: string | null, b: string | null) => countryRank(a) - countryRank(b) || (a ?? "").localeCompare(b ?? "", "de");

/** What a wine is called on the card: Schaumwein is Sekt, Rot is Rotwein. */
export const CARD_COLOR: Record<string, string> = { Schaumwein: "Sekt", Weiß: "Weißwein", Rosé: "Roséwein", Orange: "Orangewein", Rot: "Rotwein", Süßwein: "Süßwein", Likörwein: "Likörwein" };

/** The wine list: wines by the glass, then by the bottle. A part without wines is left out. */
export function drinksCard(articles: ExistingArticle[], options: CardOptions): CardPart[] {
  if (options.private) {
    const year = (v: unknown) => (typeof v === "number" ? v : null);
    const on = options.profiles ?? CELLAR_PROFILES;
    return PROFILES.filter((p) => on.includes(p.id))
      .map((p) => ({
        title: PROFILE_TITLES[p.id]!,
        sections: sections(articles, options.withoutStock, p.id === "wein" ? options.grouping : "color", (f, stock) => [
          { ...bottleOf(f, p), price: null, bottles: stock, drinkFrom: year(f.drinkFrom), drinkTo: year(f.drinkTo) },
        ], p),
      }))
      .filter((part) => part.sections.length > 0);
  }
  const custom = options.measures ?? [];
  const rubrics = options.rubrics ?? DEFAULT_RUBRICS;
  const rubricOf = (f: Record<string, unknown>) => (typeof f.rubric === "string" && rubrics.some((r) => r.id === f.rubric) ? f.rubric : null);
  const glasses = (f: Record<string, unknown>, profile: Profile) =>
    Array.isArray(f.byGlass)
      ? (f.byGlass as GlassPrice[]).map((g) => ({
          size: glassSize(g.ml, { profile: profile.measures ?? WINE_MEASURES, kind: str(f.color) ?? undefined, custom, decimal: options.decimal === true }),
          ml: g.ml,
          price: g.price,
        }))
      : [];
  /** The bottle of a drink that is not wine: „Flasche 0,33 l“, „Dose 0,5 l“; a keg is not sold whole. */
  const whole = (f: Record<string, unknown>, profile: Profile) => (typeof f.salePrice === "number" && !isBulk(f) ? [{ ...container(f, profile), price: f.salePrice }] : []);
  const container = (f: Record<string, unknown>, profile: Profile) => {
    const b = bottleOf(f, profile);
    const c = typeof f.container === "string" && f.container !== "" ? f.container : "Flasche";
    return { ...b, size: `${c} ${b.size}`, usual: -1 };
  };
  const all: Record<string, CardPart> = {};
  const fieldsOf = new Map(articles.map((a) => [a.id, a.fields]));
  /** Prepared drinks placed in a part of others, in a section of their own kind. */
  const addRecipes = (secs: CardSection[], key: string) => {
    for (const r of (options.recipes ?? []).filter((x) => !x.offCard && x.glasses.length && recipePlace(x) === key)) {
      const title = RECIPE_SECTION[r.kind] ?? r.kind;
      let sec = secs.find((x) => x.title === title);
      if (!sec) secs.push((sec = { title, groups: [{ title: null, entries: [] }] }));
      sec.groups[0]!.entries.push(recipeEntry(r, custom, fieldsOf));
    }
  };
  // The wines: open ones (without a rubric: then the open offer is there), then bottles as before.
  all.offen = { key: "offen", title: "Offene Weine", open: true, sections: sections(articles, options.withoutStock, "color", (f) => (rubricOf(f) ? [] : glasses(f, profileById("wein")))) };
  addRecipes(all.offen.sections, "offen");
  all.flasche = {
    key: "flasche",
    title: "Flaschenweine",
    note: "je Flasche 0,75 l, wenn nicht anders angegeben",
    sections: sections(articles, options.withoutStock, options.grouping, (f) => (typeof f.salePrice === "number" ? [{ ...bottleOf(f), price: f.salePrice }] : [])),
  };
  // The other profiles: glasses and bottles of a drink in one entry, by kind.
  for (const id of ["alkoholfrei", "bier", "weitere", "spirituosen"]) {
    const profile = profileById(id);
    const offers = (f: Record<string, unknown>) => (rubricOf(f) ? [] : [...glasses(f, profile), ...whole(f, profile)]);
    const secs = sections(articles, options.withoutStock, "color", offers, profile);
    addRecipes(secs, id);
    all[id] = { key: id, title: PART_TITLES[id]!, sections: secs };
  }
  // The rubrics: drinks of any profile with this rubric (a wine only with its glasses), and prepared drinks.
  for (const r of rubrics) {
    const key = `rubric:${r.id}`;
    const entries: CardEntry[] = [];
    for (const p of PROFILES) {
      const offers = (f: Record<string, unknown>) =>
        rubricOf(f) !== r.id ? [] : p.id === "wein" ? glasses(f, p) : [...glasses(f, p), ...whole(f, p)];
      for (const s of sections(articles, options.withoutStock, "number", offers, p)) for (const g of s.groups) entries.push(...g.entries);
    }
    for (const rec of options.recipes ?? []) if (!rec.offCard && rec.glasses.length && recipePlace(rec) === key) entries.push(recipeEntry(rec, custom, fieldsOf));
    entries.sort((x, y) => byNumber({ listNo: x.lines[0]?.listNo ?? null, key: x.wine }, { listNo: y.lines[0]?.listNo ?? null, key: y.wine }));
    all[key] = { key, title: r.title, sections: entries.length ? [{ title: "", groups: [{ title: null, entries }] }] : [] };
  }
  const named = (t: string) => options.headings?.[t] ?? t;
  return partOrder(options.order, rubrics)
    .map((k) => all[k])
    .filter((p): p is CardPart => Boolean(p) && p!.sections.length > 0)
    .map((p) => withAllergenNote({ ...p, title: named(p.title), sections: p.sections.map((s) => ({ ...s, title: s.title ? named(s.title) : s.title })) }));
}

/** A prepared drink as an entry: its name, its glasses as lines. */
function recipeEntry(r: Recipe, custom: readonly CustomMeasure[], fieldsOf: ReadonlyMap<string, Record<string, unknown>>): CardEntry {
  return {
    producer: "",
    wine: r.name,
    color: r.kind,
    country: null,
    region: null,
    appellation: null,
    grapes: [],
    taste: null,
    organic: false,
    text: r.cardText,
    lines: r.glasses.map((g) => ({ article: r.id, listNo: r.listNo, vintage: null, size: glassSize(g.ml, { custom }), ml: g.ml, price: g.price })),
    notes: [...new Set(r.additives.map(additiveNumber).filter((n) => n > 0))].sort((x, y) => x - y),
    allergens: allergenNames(r.allergens),
    // A juice spritzer and a Mocktail, unless a part of it has alcohol (a wine spritzer has 6 to 7 % vol.).
    alcoholFree: freeKind(r.kind) && r.components.every((c) => { const f = fieldsOf.get(c.article); return !f || freeOfAlcohol({ ...f, profile: profileOf(f) }); }),
    recipe: true,
    plain: true,
  };
}

/** When every entry of a part has an allergen (wine: Sulfite), it is said once under the title (`common`). */
function withAllergenNote(p: CardPart): CardPart {
  const entries = p.sections.flatMap((s) => s.groups.flatMap((g) => g.entries));
  if (!entries.length) return p;
  const common = (entries[0]!.allergens ?? []).filter((a) => entries.every((e) => e.allergens?.includes(a)));
  if (!common.length) return p;
  return { ...p, common };
}

/** The line under a part's title: its note and, with the allergens on the card, what all its drinks contain. */
export const partNote = (p: CardPart, allergens = true) => [p.note, allergens && p.common?.length ? `Enthält ${p.common.join(", ")}` : null].filter(Boolean).join(" · ");

/** The allergens an entry shows itself: those not said for its whole part. */
export const ownAllergens = (e: CardEntry, p: CardPart) => (e.allergens ?? []).filter((a) => !p.common?.includes(a));

/** The list for the staff: every drink on the card with its allergens (docs/17-getraenke.md, Pflichtangaben). */
export function allergenRows(parts: CardPart[]): { part: string; name: string; allergens: string[] }[] {
  return parts.flatMap((p) => p.sections.flatMap((s) => s.groups.flatMap((g) => g.entries.map((e) => ({ part: p.title, name: [e.producer, e.wine].filter(Boolean).join(" "), allergens: e.allergens ?? [] })))));
}

/** The additives on a card, by footnote number, for the footer: „1 mit Farbstoff · 8 koffeinhaltig“. */
export function cardFootnotes(parts: CardPart[]): { n: number; text: string }[] {
  const used = new Set<number>();
  for (const p of parts) for (const s of p.sections) for (const g of s.groups) for (const e of g.entries) for (const n of e.notes ?? []) used.add(n);
  return [...used].sort((a, b) => a - b).map((n) => ({ n, text: ADDITIVES[n - 1]! }));
}

/**
 * The rule of the restaurant law, as a hint (never a block): at least one alcohol-free drink must
 * not cost more than the cheapest alcoholic one, per litre. Null when it holds or cannot be said.
 */
export function cheapestRule(parts: CardPart[]): { free: { name: string; perLitre: number }; alcoholic: { name: string; perLitre: number } } | null {
  let free: { name: string; perLitre: number } | null = null;
  let alcoholic: { name: string; perLitre: number } | null = null;
  for (const p of parts)
    for (const s of p.sections)
      for (const g of s.groups)
        for (const e of g.entries)
          for (const l of e.lines) {
            const ml = l.ml ?? l.bottle;
            if (l.price === null || !ml) continue;
            const x = { name: [e.producer, e.wine].filter(Boolean).join(" "), perLitre: Math.round((l.price * 1000) / ml) };
            if (e.alcoholFree) free = !free || x.perLitre < free.perLitre ? x : free;
            else alcoholic = !alcoholic || x.perLitre < alcoholic.perLitre ? x : alcoholic;
          }
  return free && alcoholic && free.perLitre > alcoholic.perLitre ? { free, alcoholic } : null;
}

/** A bottle line's size: in ml and as shown („0,75 l“, „Magnum 1,5 l“), with the profile's usual size. */
function bottleOf(f: Record<string, unknown>, profile: Profile = profileById("wein")): { size: string; bottle: number; usual: number } {
  const ml = sizeOf(f) ?? 750;
  return { size: measureName(ml, "size", { profile: profile.measures ?? WINE_MEASURES }), bottle: ml, usual: profile.usualSize ?? 0 };
}

/** What a line says about its size where it is not the usual bottle: „1/8 l“, „Magnum 1,5 l“; else "". */
export const lineSize = (l: CardLine) => (l.ml !== undefined || (l.bottle !== undefined && l.bottle !== (l.usual ?? 750)) ? l.size : "");

function sections(
  articles: ExistingArticle[],
  withoutStock: boolean | undefined,
  grouping: CardGrouping,
  offers: (f: Record<string, unknown>, stock: number) => Omit<CardLine, "article" | "listNo" | "vintage">[],
  profile: Profile = profileById("wein"),
): CardSection[] {
  // One entry per wine family; its vintages and sizes (or glasses) become lines.
  const entries = new Map<string, CardEntry & { first: { listNo: string | null; key: string } }>();
  for (const a of articles) {
    const f = a.fields;
    if (typeof f.producer !== "string" || profileOf(f) !== profile.id || f.offCard === true) continue;
    if (!withoutStock && a.stock <= 0) continue;
    const key = str(f.family) ?? a.id;
    for (const offer of offers(f, a.stock)) {
      const line: CardLine = {
        article: a.id,
        listNo: str(f.listNo),
        vintage: typeof f.vintage === "number" ? f.vintage : null,
        ...offer,
      };
      let e = entries.get(key);
      if (!e) {
        e = {
          producer: str(f.producer)!,
          wine: [str(f.wine), str(f.age)].filter(Boolean).join(" "),
          color: str(f.color) ?? (profile.id === "wein" ? "Rot" : "Sonstiges"),
          country: str(f.country),
          region: str(f.region),
          appellation: str(f.appellation),
          grapes: Array.isArray(f.grapes) ? f.grapes.filter((g): g is string => typeof g === "string") : [],
          taste: str(f.taste),
          organic: f.organic === true,
          text: str(f.cardText),
          lines: [],
          notes: [...new Set((Array.isArray(f.additives) ? (f.additives as string[]) : []).map(additiveNumber).filter((n) => n > 0))].sort((x, y) => x - y),
          allergens: allergenNames(f.allergens),
          alcoholFree: freeOfAlcohol({ ...f, profile: profileOf(f) }),
          ...(profile.id === "wein" ? {} : { plain: true }),
          quality: str(f.quality),
          analysis: analysisOf(f),
          first: { listNo: line.listNo, key: `${f.producer} ${f.wine}` },
        };
        entries.set(key, e);
      } else if (!e.lines.some((x) => x.article === a.id)) {
        // Another vintage or size: only what all of them share stays on the card.
        if (e.quality !== str(f.quality)) e.quality = null;
        if (e.analysis !== analysisOf(f)) e.analysis = null;
      }
      e.lines.push(line);
      if (f.container === "Fass" && offer.ml !== undefined && !e.extra?.includes("vom Fass")) e.extra = [...(e.extra ?? []), "vom Fass"];
      if (byNumber({ listNo: line.listNo, key: "" }, { listNo: e.first.listNo, key: "" }) < 0) e.first.listNo = line.listNo;
    }
  }
  const all = [...entries.values()];
  for (const e of all) e.lines.sort((a, b) => byNumber({ listNo: a.listNo, key: "" }, { listNo: b.listNo, key: "" }) || Number(a.ml === undefined) - Number(b.ml === undefined) || (a.ml ?? 0) - (b.ml ?? 0) || (a.vintage ?? 0) - (b.vintage ?? 0) || (a.bottle ?? 0) - (b.bottle ?? 0));
  all.sort((a, b) => byNumber(a.first, b.first));
  const clean = (e: (typeof all)[number]): CardEntry => {
    const { first: _, ...entry } = e;
    return entry;
  };

  const kinds = profile.fields.find((f) => f.key === profile.categoryField)?.options ?? [];
  const colors = profile.id === "wein" ? [...CARD_COLORS, ...WINE_COLORS.filter((c) => !CARD_COLORS.includes(c))] : [...kinds];
  const bySection = (title: (e: CardEntry) => string, order: (a: string, b: string) => number) => {
    const map = new Map<string, CardEntry[]>();
    for (const e of all) map.set(title(e), [...(map.get(title(e)) ?? []), clean(e)]);
    return [...map.keys()].sort(order).map((t) => ({ title: t, entries: map.get(t)! }));
  };
  const colorOrder = (a: string, b: string) => (colors.indexOf(a) + 1 || 99) - (colors.indexOf(b) + 1 || 99);
  const split = (entries: CardEntry[], by: (e: CardEntry) => string | null, order: (a: string | null, b: string | null) => number): CardGroup[] => {
    const map = new Map<string | null, CardEntry[]>();
    for (const e of entries) map.set(by(e), [...(map.get(by(e)) ?? []), e]);
    return [...map.keys()].sort(order).map((title) => ({ title, entries: map.get(title)! }));
  };

  switch (grouping) {
    case "number":
      return [{ title: "Weine", groups: [{ title: null, entries: all.map(clean) }] }];
    case "color":
      return bySection((e) => e.color, colorOrder).map((s) => ({ title: SECTION_TITLE[s.title] ?? s.title, groups: [{ title: null, entries: s.entries }] }));
    case "colorOrigin":
      return bySection((e) => e.color, colorOrder).map((s) => ({
        title: SECTION_TITLE[s.title] ?? s.title,
        groups: split(s.entries, (e) => e.country, byCountry),
      }));
    case "origin":
      return bySection((e) => e.country ?? "Weitere Herkunft", (a, b) => byCountry(a === "Weitere Herkunft" ? null : a, b === "Weitere Herkunft" ? null : b)).map((s) => ({
        title: s.title,
        groups: split(s.entries, (e) => e.region, (a, b) => (a ?? "~").localeCompare(b ?? "~", "de")),
      }));
  }
}

/**
 * What a printed card said, line by line (docs/15-getraenkekarte.md, Veraltete Karte): kept when the
 * card is printed, compared with the card as it would be now. The value is the price in cents, or
 * the bottles on the list of a private cellar.
 */
export interface PrintedLine {
  /** Wine and offer: "<article>|bottle" or "<article>|<ml>". */
  key: string;
  /** "Weingut Lindenhof Riesling Kabinett 2023 (1/8 l)". */
  name: string;
  value: number | null;
}

export function cardDigest(parts: CardPart[]): PrintedLine[] {
  const out: PrintedLine[] = [];
  for (const p of parts)
    for (const s of p.sections)
      for (const g of s.groups)
        for (const e of g.entries)
          for (const l of e.lines) {
            const size = lineSize(l) ? ` (${lineSize(l)})` : "";
            out.push({ key: `${l.article}|${l.ml ?? "bottle"}`, name: `${e.producer} ${e.wine}${l.vintage !== null ? ` ${l.vintage}` : ""}${size}`, value: l.price ?? l.bottles ?? null });
          }
  return out.sort((a, b) => a.name.localeCompare(b.name, "de"));
}

export interface CardDiff {
  added: PrintedLine[];
  gone: PrintedLine[];
  /** The line now, with the value printed. */
  changed: { line: PrintedLine; was: number | null }[];
}

/** What changed since the card was printed; three empty lists: the printed card is up to date. */
export function cardDiff(printed: readonly PrintedLine[], now: readonly PrintedLine[]): CardDiff {
  const before = new Map(printed.map((l) => [l.key, l]));
  const after = new Map(now.map((l) => [l.key, l]));
  return {
    added: now.filter((l) => !before.has(l.key)),
    gone: printed.filter((l) => !after.has(l.key)),
    changed: now.filter((l) => before.has(l.key) && before.get(l.key)!.value !== l.value).map((line) => ({ line, was: before.get(line.key)!.value })),
  };
}

export const isUpToDate = (d: CardDiff) => d.added.length + d.gone.length + d.changed.length === 0;
